// packages/shared/src/constants/ptSpecialTests.ts
// Default special test catalog items for Physical Therapy.
// All items use category 'special_test', data_type 'select' with standard result options.

import type { FlowSheetItemCatalogEntry } from '../types/models/flowSheet';

const ts = 0; // placeholder timestamps for defaults

const SELECT_OPTIONS = ['Positive', 'Negative', 'Equivocal', 'Not Tested'];

/** Helper to build a special test catalog entry */
function entry(
  id: string,
  label: string,
  body_regions: string[],
  bilateral: boolean = false
): FlowSheetItemCatalogEntry {
  return {
    id,
    organization_id: 'system',
    label,
    data_type: 'select',
    category: 'special_test',
    select_options: SELECT_OPTIONS,
    body_regions,
    bilateral,
    is_active: true,
    created_at: ts,
    updated_at: ts,
  };
}

// ── Shoulder Special Tests (~9) ──────────────────────────────────────

const SHOULDER_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('st_neer', "Neer's Test", ['shoulder'], true),
  entry('st_hawkins', "Hawkins-Kennedy Test", ['shoulder'], true),
  entry('st_empty_can', "Empty Can (Jobe's) Test", ['shoulder'], true),
  entry('st_full_can', "Full Can Test", ['shoulder'], true),
  entry('st_drop_arm', "Drop Arm Test", ['shoulder'], true),
  entry('st_obrien', "O'Brien's Test", ['shoulder'], true),
  entry('st_speed', "Speed's Test", ['shoulder'], true),
  entry('st_yergason', "Yergason's Test", ['shoulder'], true),
  entry('st_apprehension_shoulder', "Apprehension Test (Shoulder)", ['shoulder'], true),
];

// ── Knee Special Tests (~5) ──────────────────────────────────────────

const KNEE_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('st_lachman', "Lachman's Test", ['knee'], true),
  entry('st_anterior_drawer_knee', "Anterior Drawer (Knee)", ['knee'], true),
  entry('st_posterior_drawer_knee', "Posterior Drawer (Knee)", ['knee'], true),
  entry('st_mcmurray', "McMurray's Test", ['knee'], true),
  entry('st_valgus_stress', "Valgus Stress Test", ['knee'], true),
  entry('st_varus_stress', "Varus Stress Test", ['knee'], true),
];

// ── Lumbar Special Tests (~5) ────────────────────────────────────────

const LUMBAR_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('st_slr', "Straight Leg Raise (SLR)", ['lumbar'], true),
  entry('st_slump', "Slump Test", ['lumbar']),
  entry('st_prone_instability', "Prone Instability Test", ['lumbar']),
  entry('st_centralization', "Centralization / Peripheralization", ['lumbar']),
  entry('st_thomas', "Thomas Test", ['lumbar', 'hip'], true),
];

// ── Cervical Special Tests (~4) ──────────────────────────────────────

const CERVICAL_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('st_spurling', "Spurling's Test", ['cervical'], true),
  entry('st_distraction', "Distraction Test (Cervical)", ['cervical']),
  entry('st_upper_limb_tension', "Upper Limb Tension Test (ULTT)", ['cervical', 'shoulder'], true),
  entry('st_sharp_purser', "Sharp-Purser Test", ['cervical']),
];

// ── Hip Special Tests (~6) ───────────────────────────────────────────

const HIP_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('st_faber', "FABER (Patrick's) Test", ['hip'], true),
  entry('st_fadir', "FADIR Test", ['hip'], true),
  entry('st_ober', "Ober's Test", ['hip'], true),
  entry('st_trendelenburg', "Trendelenburg Test", ['hip'], true),
  entry('st_log_roll', "Log Roll Test", ['hip'], true),
  entry('st_piriformis', "Piriformis Test", ['hip', 'lumbar'], true),
];

// ── Ankle Special Tests (~4) ─────────────────────────────────────────

const ANKLE_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('st_anterior_drawer_ankle', "Anterior Drawer (Ankle)", ['ankle'], true),
  entry('st_talar_tilt', "Talar Tilt Test", ['ankle'], true),
  entry('st_thompson', "Thompson Test (Achilles)", ['ankle'], true),
  entry('st_windlass', "Windlass Test (Plantar Fascia)", ['ankle'], true),
];

// ── Elbow Special Tests (~4) ─────────────────────────────────────────

const ELBOW_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('st_cozen', "Cozen's Test (Lateral Epicondylitis)", ['elbow'], true),
  entry('st_mill', "Mill's Test", ['elbow'], true),
  entry('st_valgus_stress_elbow', "Valgus Stress Test (Elbow)", ['elbow'], true),
  entry('st_tinels_elbow', "Tinel's Test (Elbow)", ['elbow'], true),
];

// ── Wrist/Hand Special Tests (~4) ────────────────────────────────────

const WRIST_HAND_TESTS: FlowSheetItemCatalogEntry[] = [
  entry('st_phalen', "Phalen's Test", ['wrist'], true),
  entry('st_tinels_wrist', "Tinel's Test (Wrist)", ['wrist'], true),
  entry('st_finkelstein', "Finkelstein's Test", ['wrist'], true),
  entry('st_grind', "Grind Test (CMC)", ['hand'], true),
];

// ── Full special test catalog ────────────────────────────────────────

export const PT_SPECIAL_TEST_DEFAULTS: FlowSheetItemCatalogEntry[] = [
  ...SHOULDER_TESTS,
  ...KNEE_TESTS,
  ...LUMBAR_TESTS,
  ...CERVICAL_TESTS,
  ...HIP_TESTS,
  ...ANKLE_TESTS,
  ...ELBOW_TESTS,
  ...WRIST_HAND_TESTS,
];

/** All special test IDs for convenience (used in template catalogItemIds) */
export const PT_SPECIAL_TEST_IDS: string[] = PT_SPECIAL_TEST_DEFAULTS.map(item => item.id);
